/**
 * AddRfiModal — create or edit an RFI (Supabase), scoped to a construction contract.
 *
 * Opens via CustomEvent('open-add-rfi-modal', { detail }):
 *   create: { contractId, projectId }
 *   edit:   { contractId, projectId, rfiId, prefill: {...} }
 *
 * Busts ctx.rfiCache[contractId] + dispatches outline-render on success.
 */

import { useState, useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { createRfi, updateRfi, getNextRfiNumber, RFI_STATUS_LABELS, RFI_REVIEWING_PARTIES, RFI_ORIGINS, getRfiAttachments, getRfiAttachmentUploadUrl, confirmRfiAttachmentUpload, deleteRfiAttachment } from '../services/rfis.js'
import Select from './Select.jsx'
import MarkdownField from './MarkdownField.jsx'
import { closeThread, reopenThread } from '../services/threads.js'
import { getProjectPhases } from '../services/phases.js'
import { getMilestoneTemplates, createMilestone } from '../services/milestones.js'
import { seedMilestoneChecklist } from '../services/checklist.js'
import { RFI_RESPONSE_TEMPLATE_ID } from '../outline/constants.js'
import { rideSupport } from '../services/passengers.js'

const STATUS_OPTIONS = Object.keys(RFI_STATUS_LABELS).map(v => ({ value: v, label: RFI_STATUS_LABELS[v] }))

function isoDate(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

/**
 * Create the RFI's milestone from the 'RFI Response' deliverable template — same recipe as
 * AddAsiModal's createAsiMilestone (createMilestone + checklist scaffold; no assignment — crews
 * are added on the Flight Board). Returns the new milestone Airtable ID, or null on any failure
 * (non-fatal — the RFI is still created either way).
 */
async function createRfiMilestone(projectId, rfiNumber, subject) {
  const tpls = await getMilestoneTemplates()
  const tpl = (tpls || []).find(t => t.id === RFI_RESPONSE_TEMPLATE_ID)
  if (!tpl) return null

  let phases = ctx.state?.phases || []
  let csPhase = phases.find(p => ((p.fields['Phase Name']) || '').indexOf('Construction Support') !== -1)
  if (!csPhase) {
    try { csPhase = (await getProjectPhases(projectId)).find(p => ((p.fields['Phase Name']) || '').indexOf('Construction Support') !== -1) }
    catch (_) { /* non-fatal */ }
  }
  const phaseName = csPhase?.fields?.['Phase Name'] || 'Construction Support Phase'

  const today = new Date()
  const lftDate = new Date(today)
  lftDate.setDate(lftDate.getDate() + 7)
  const defDur = Math.max(1, Math.ceil(parseFloat(tpl.fields['Default Days Budgeted'] || 1)))

  const delFields = {
    'Project':               [projectId],
    'Deliverable':           [tpl.id],
    'Project Phase':         phaseName,
    'Latest Finish (LFT)':   isoDate(lftDate),
    'Earliest Start (EST)':  isoDate(today),
    'Duration':              defDur,
    'Created by Automation': true,
    'Create Slack Thread':   true,
    'Subtitle':              ('RFI ' + (rfiNumber != null ? '#' + rfiNumber : '') + (subject ? ' — ' + subject : '')).trim(),
  }
  if (csPhase) delFields['Phase'] = [csPhase.id]

  const newId = await createMilestone(delFields)

  try {
    await seedMilestoneChecklist(newId)
  } catch (_) { /* non-fatal */ }

  return newId
}

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const areaStyle = { ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }

export default function AddRfiModal() {
  const [open, setOpen]             = useState(false)
  const [contractId, setContractId] = useState(null)
  const [projectId, setProjectId]   = useState(null)
  const [editId, setEditId]         = useState(null)
  const [submissionId, setSubmissionId] = useState(null)  // contractor-portal intake item being accepted
  const [number, setNumber]         = useState('')
  const [subject, setSubject]       = useState('')
  const [status, setStatus]         = useState('open')
  const [origin, setOrigin]         = useState('')
  const [parties, setParties]       = useState([])
  const [question, setQuestion]     = useState('')
  const [received, setReceived]     = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)
  // Thread lifecycle prompt: when an edit transitions status and the RFI has a
  // linked thread, ask whether to log the matching thread activity too.
  const [origStatus, setOrigStatus] = useState('open')
  const [threadAtId, setThreadAtId] = useState(null)
  const [msAtId, setMsAtId]         = useState(null)
  const [threadPrompt, setThreadPrompt] = useState(null) // null | 'close' | 'reopen'
  // Attachments
  const [stagedFiles, setStagedFiles]           = useState([]) // File[] — to upload on submit
  const [existingAttachments, setExistingAttachments] = useState([]) // loaded for edit mode
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setContractId(d.contractId || null)
      setProjectId(d.projectId || null)
      setEditId(d.rfiId || null)
      setSubmissionId(d.submissionId || null)
      const p = d.prefill || {}
      setNumber(p.rfi_number != null ? String(p.rfi_number) : '')
      // Create: auto-assign the next RFI number in sequence (never typed by hand).
      if (!d.rfiId) {
        getNextRfiNumber(d.projectId || null, d.contractId || null)
          .then(n => { if (n != null) setNumber(String(n)) })
          .catch(() => {})
      }
      setSubject(p.subject || '')
      setStatus(p.status || 'open')
      setOrigin(p.origin || '')
      setParties(Array.isArray(p.reviewing_party) ? p.reviewing_party : [])
      setQuestion(p.question || '')
      // Default "Received from GC" to today on create (mirrors AddSubmittalModal) — edits keep their value.
      setReceived(p.date_received || (d.rfiId ? '' : new Date().toISOString().slice(0, 10)))
      setOrigStatus(p.status || 'open')
      setThreadAtId(p.thread_airtable_id || null)
      setMsAtId(p.milestone_airtable_id || null)
      setThreadPrompt(null)
      setStagedFiles([])
      setExistingAttachments([])
      setError(null); setSubmitting(false); setOpen(true)
      // Load existing attachments for edit mode
      if (d.rfiId) {
        getRfiAttachments(d.rfiId).then(setExistingAttachments).catch(() => {})
      }
    }
    window.addEventListener('open-add-rfi-modal', onOpen)
    return () => window.removeEventListener('open-add-rfi-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function toggleParty(p) {
    setParties(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])
  }

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = {
      rfi_number: number ? parseInt(number, 10) : null,
      subject: subject || null,
      status,
      origin: origin || null,
      reviewing_party: parties.length ? parties : null,
      question: question || null,
      date_received: received || null,
    }
    try {
      let rfiId = editId
      if (editId) {
        await updateRfi(editId, fields)
      } else {
        if (!contractId) { setError('No contract in context.'); setSubmitting(false); return }
        // An RFI is a THREAD riding the project's RFI Support milestone (Matt, 2026-10-24: one RFI is
        // too small to be a milestone). The support milestone is created the first time it's needed.
        let milestoneId = null, threadId = null
        if (projectId) {
          try {
            const r = await rideSupport(projectId, 'rfi', ('RFI ' + (fields.rfi_number != null ? '#' + fields.rfi_number : '') + (fields.subject ? ' — ' + fields.subject : '')).trim(), fields.question || '', ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Member Name'])
            threadId = r && r.threadId; milestoneId = r && r.milestoneId
          } catch (_) { /* non-fatal — RFI still created; the thread can be linked later */ }
        }
        const newRfi = await createRfi(contractId, projectId, { ...fields, thread_airtable_id: threadId })
        rfiId = newRfi?.id || null
        // Reload project data so the new milestone appears in the schedule immediately.
        if (milestoneId && projectId && ctx.reloadProjectData) ctx.reloadProjectData(projectId)
      }

      // Upload any staged attachments (best-effort — failures don't block the save)
      if (rfiId && stagedFiles.length) {
        for (const file of stagedFiles) {
          try {
            const { signedUrl, storagePath, publicUrl } = await getRfiAttachmentUploadUrl(rfiId, file.name)
            await fetch(signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } })
            const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : null
            await confirmRfiAttachmentUpload(rfiId, storagePath, publicUrl, file.name, ext)
          } catch (uploadErr) {
            console.warn('[AddRfiModal] attachment upload failed:', uploadErr.message)
          }
        }
      }

      if (ctx.rfiCache && contractId) ctx.rfiCache[contractId] = undefined
      // This RFI may ALSO be mounted (now stale) under its milestone or thread — see the
      // identical fix note on AsiNode.applyUpdated in ConstructionSection.jsx.
      if (ctx.milestoneRfiCache && msAtId) ctx.milestoneRfiCache[msAtId] = undefined
      if (ctx.threadCaItemCache && threadAtId) ctx.threadCaItemCache[threadAtId] = undefined
      window.dispatchEvent(new Event('outline-render'))
      // Edited status transition + linked thread → offer the matching thread activity
      const wasClosed = origStatus === 'closed' || origStatus === 'withdrawn'
      const nowClosed = status === 'closed' || status === 'withdrawn'
      if (editId && threadAtId && wasClosed !== nowClosed) {
        setSubmitting(false)
        setThreadPrompt(nowClosed ? 'close' : 'reopen')
        return
      }
      setOpen(false)
    } catch (ex) {
      setError(ex.message || 'Failed to save RFI'); setSubmitting(false)
    }
  }

  function handleFileChange(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setStagedFiles(prev => [...prev, ...files])
    e.target.value = ''
  }

  async function handleDeleteExisting(attachmentId) {
    try {
      await deleteRfiAttachment(attachmentId)
      setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId))
    } catch (err) {
      console.warn('[AddRfiModal] delete attachment failed:', err.message)
    }
  }

  async function handleThreadPrompt(confirm) {
    const kind = threadPrompt
    setThreadPrompt(null)
    setOpen(false)
    if (!confirm || !threadAtId) return
    try {
      if (kind === 'close') await closeThread(threadAtId, ctx.currentMemberId || null, 'RFI marked ' + status + '.')
      else await reopenThread(threadAtId, ctx.currentMemberId || null, 'RFI reopened.')
      // Flip caches so status pills update instantly
      const cached = Array.isArray(ctx.threadCache) ? ctx.threadCache.find(t => t.id === threadAtId) : null
      if (cached) {
        cached.fields['Closed'] = kind === 'close'
        cached.fields['Status'] = kind === 'close' ? '⚪ Closed' : '🟢 Open'
      }
      const caCached = ctx.caThreadCache?.[threadAtId]
      if (caCached && caCached.fields) {
        caCached.fields['Closed'] = kind === 'close'
        caCached.fields['Status'] = kind === 'close' ? '⚪ Closed' : '🟢 Open'
      }
      if (ctx.threadActCache) ctx.threadActCache[threadAtId] = undefined
      window.dispatchEvent(new Event('outline-render'))
    } catch (_) { /* non-fatal */ }
  }

  if (!open) return null

  // Thread lifecycle prompt — replaces the form after a status-transition save
  if (threadPrompt) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div data-modal-id="AddRfiModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 400, padding: '20px 20px 16px' }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 8 }}>
            {threadPrompt === 'close' ? 'Close the linked thread?' : 'Reopen the linked thread?'}
          </div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 16 }}>
            {threadPrompt === 'close'
              ? 'The RFI is now ' + (RFI_STATUS_LABELS[status] || status).toLowerCase() + '. Record a "Thread Closed" activity on its linked thread as well?'
              : 'The RFI is open again. Record a "Thread Reopened" activity on its linked thread as well?'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => handleThreadPrompt(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>No, leave it</button>
            <button onClick={() => handleThreadPrompt(true)} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              {threadPrompt === 'close' ? 'Close Thread' : 'Reopen Thread'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddRfiModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editId ? 16 : 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{editId ? 'Edit RFI' : 'Add RFI'}</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        {!editId && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginBottom: 12 }}>
            An RFI Response milestone will be created automatically in Construction Support.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 90 }}>
              <label style={labelStyle}>RFI #</label>
              <input type="number" disabled value={number} placeholder="auto"
                style={{ ...inputStyle, background: 'var(--aj-bg-muted)', color: 'var(--aj-text-mid)', cursor: 'not-allowed' }}
                title="Auto-assigned — next number in sequence" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Status</label>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Subject</label>
            <input style={inputStyle} value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Origin</label>
            <Select value={origin} onChange={setOrigin}
              options={[{ value: '', label: '— None —' }].concat(RFI_ORIGINS.map(o => ({ value: o, label: o })))}
              placeholder="— None —" style={{ width: '100%', fontSize: 12 }} />
          </div>
          <div>
            <label style={labelStyle}>Reviewing Party</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {RFI_REVIEWING_PARTIES.map(p => {
                const on = parties.includes(p)
                return (
                  <button key={p} onClick={() => toggleParty(p)} style={{
                    padding: '4px 8px', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 11, cursor: 'pointer',
                    border: '0.5px solid ' + (on ? '#07272D' : 'var(--aj-border-mid)'), background: on ? '#07272D' : 'var(--aj-bg-elevated)', color: on ? '#fff' : 'var(--aj-text)',
                  }}>{p}</button>
                )
              })}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Question</label>
            <MarkdownField value={question} onChange={setQuestion} />
          </div>
          <div>
            <label style={labelStyle}>Received from GC</label>
            <input type="date" style={inputStyle} value={received} onChange={e => setReceived(e.target.value)} />
          </div>

          {/* Attachments */}
          <div>
            <label style={labelStyle}>Attachments</label>
            {/* Existing attachments (edit mode) */}
            {existingAttachments.map(att => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                <span style={{ fontSize: 13, color: 'var(--aj-text-mid)', flexShrink: 0 }}>📎</span>
                <a href={att.url} target="_blank" rel="noopener noreferrer"
                  style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={att.name}>{att.name}</a>
                <button onClick={() => handleDeleteExisting(att.id)} title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
              </div>
            ))}
            {/* Staged files (not yet uploaded) */}
            {stagedFiles.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                <span style={{ fontSize: 13, color: 'var(--aj-text-mid)', flexShrink: 0 }}>📄</span>
                <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={f.name}>{f.name}</span>
                <button onClick={() => setStagedFiles(prev => prev.filter((_, j) => j !== i))} title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
              </div>
            ))}
            {/* Attach button */}
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileChange} />
            <button onClick={() => fileInputRef.current?.click()}
              style={{ marginTop: existingAttachments.length || stagedFiles.length ? 6 : 0, padding: '5px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer' }}>
              + Attach files
            </button>
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add RFI')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
